import {ShiftMakerSettings} from "../settings/shift-maker-settings";
import {LocationNl} from "../../../../models/location-nl";
import {LocationDto} from "../../../../models/location-dto";
import {EventNl} from "../../../../models/event/event-nl";
import {EmployeeEventResponseDto} from "../../../../models/event/employee-event-response-dto";
import {EmployeeNl} from "../../x-models/employee-nl";

/**
 * How much of the shift plan a response actually carries — mirrors `ShiftPlanScopeNl` (#409).
 *
 * A narrowed payload and an empty day look identical to a browser. This is what lets the screen tell the two
 * apart, so a shift lead reads "you are seeing the event you are leading" rather than concluding it is broken.
 */
export class ShiftPlanScope {
  /** `true` when the payload was narrowed to {@link eventId} — i.e. the session is a shift lead. */
  bounded: boolean = false;
  /** The single event the payload was narrowed to, `null`/absent when unbounded. */
  eventId?: string | null;
  /** Start of the window the session holds (event start minus the lead-in). */
  from?: Date | null;
  /** End of the window the session holds (event end plus the wind-down). */
  until?: Date | null;
}

/**
 * Everything the board needs to paint one calendar day, from `GET /v2026.8/Company/ShiftPlan/Day` (#409).
 *
 * Field-for-field this is the concatenation of the five reads the screen used to make:
 * `settings` ← `Administration/Settings/GetShiftMakerSettings`,
 * `companyLocations` ← `Inventory/Location/GetCompanyLocations`,
 * `disabledLocations` ← `Inventory/Event/GetDisabledLocations`,
 * `events` + `responses` ← the two halves of `Inventory/Event/GetEmployeeAvailability`'s tuple,
 * `employees` ← `Company/Employee/ListNl?start={date}`.
 */
export class ShiftPlanDay {
  scope: ShiftPlanScope = new ShiftPlanScope();
  settings: ShiftMakerSettings = new ShiftMakerSettings();
  companyLocations: {[shardId: string]: LocationNl[]} = {};
  disabledLocations: {[shardId: string]: LocationDto[]} = {};
  events: EventNl[] = [];
  /**
   * The events that start on this day but were kept off the board by „Im Dienstplan ausblenden".
   *
   * Carried so the screen can NAME them. Hiding an event is deliberate, but its only symptom is a column that
   * is not there — which a planner cannot tell apart from a quieter day, a mis-picked date or a bug, and
   * nothing on the board points at the setting that would explain it.
   *
   * Always empty for a `ShiftLead`: that role is entitled to exactly one event, and a hidden event is by
   * definition not that one, so the server clears the list rather than filtering it.
   */
  hiddenEvents: EventNl[] = [];
  responses: EmployeeEventResponseDto[] = [];
  employees: EmployeeNl[] = [];
}

/**
 * The month calendar's event markers, from `GET /v2026.8/Company/ShiftPlan/Calendar` (#409) — the replacement
 * for `Inventory/Event/GetPrivatePublicEvents`.
 *
 * Deliberately a second call rather than a field of {@link ShiftPlanDay}: its range is the month the calendar
 * is *showing*, not the day that is *selected*, so paging the calendar must not re-read the board and selecting
 * a day must not re-read the month.
 */
export class ShiftPlanCalendar {
  scope: ShiftPlanScope = new ShiftPlanScope();
  events: EventNl[] = [];
}
